// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { HasOnError } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import shareEntity, { ShareEntityParameter } from "@insite/client-framework/Store/CommonHandlers/ShareEntity";
import translate from "@insite/client-framework/Translate";
import { ShareEntityModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@vanmeter/App/Components/Notify";
import xLoadingOverlay from "@vanmeter/App/Handlers/XNG/LoadingOverlay";
import { OrderPdfAttachmentUrl, QuotePdfAttachmentUrl } from "@vanmeter/XDL/constants";
import { xValidateOnSubmit } from "@vanmeter/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    entityId: string;
    entityName: "Order" | "Invoice" | "Quote";
    extendedStyles?: ShareEntityButtonStyles;
    variant?: "button" | "clickable";
    /**
     * If true, the email attachment will be generated using the entity details web page, rather than a pre-defined PDF file.
     */
    generateAttachmentFromWebpage?: boolean;
    extraProperties?: SafeDictionary<string>;
    // XNG-Changes
    isInvoice?: boolean;
    isQuote?: boolean;
}

const mapDispatchToProps = {
    shareEntity,
    // XNG-Changes: add loading spinner
    xLoadingOverlay,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ShareEntityButtonStyles {
    clickable?: ClickablePresentationProps;
    button?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    emailToGridItem?: GridItemProps;
    emailToTextField?: TextFieldProps;
    emailFromGridItem?: GridItemProps;
    emailFromTextField?: TextFieldProps;
    subjectGridItem?: GridItemProps;
    subjectTextField?: TextFieldProps;
    messageGridItem?: GridItemProps;
    messageTextArea?: TextAreaProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    sendButton?: ButtonPresentationProps;
    // XNG-Changes
    notifyMsg?: InjectableCss;
    printButtonGridItem?: GridItemProps;
}

export const shareEntityButtonStyles: ShareEntityButtonStyles = {
    button: {
        color: "secondary",
    },
    modal: {
        size: 600,
    },
    emailToGridItem: {
        width: 6,
    },
    emailFromGridItem: {
        width: 6,
    },
    subjectGridItem: {
        width: 12,
    },
    messageGridItem: {
        width: 12,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    sendButton: {
        css: css`
            margin-left: 10px;
        `,
    },
};

const invoiceUrl = "/api/v1/invoices/shareinvoice";
const orderUrl = "/api/v1/orders/shareorder";
const quoteUrl = "/api/vmi/quote/sharequote";

const ShareEntityButton = ({
    entityId,
    entityName,
    extendedStyles,
    shareEntity,
    variant = "button",
    generateAttachmentFromWebpage,
    extraProperties,
    // XNG-Changes
    xLoadingOverlay,
    isInvoice,
    isQuote,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shareEntityButtonStyles, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailToError, setEmailToError] = useState("");
    const [emailFrom, setEmailFrom] = useState("");
    const [emailFromError, setEmailFromError] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [isSending, setIsSending] = useState(false);
    // Xng-Changes
    const [showSuccessMsg, setShowSuccessMsg] = useState(false);
    const [errorMsg, setErrorMsg] = useState<React.ReactNode>("");

    // Xng-Changes
    const formId = "sendEmailModalForm";

    if (!entityId) {
        return null;
    }

    let url = "";
    if (entityName === "Order") {
        url = orderUrl;
    } else if (entityName === "Invoice") {
        url = invoiceUrl;
    } else if (entityName === "Quote") {
        url = quoteUrl;
    }
    // XNG-Changes:from classic
    const printBtnLabel = `${entityName}_${entityId}.pdf`;

    const requiredFieldMessage = translate("This is a required field");
    const emailFieldMessage = translate("Enter a valid email address");

    const checkForErrors = () => {
        // Regular expression to validate email address
        const localEmailToError = !emailTo ? requiredFieldMessage : validateEmail(emailTo) ? "" : emailFieldMessage;
        setEmailToError(localEmailToError);
        const localEmailFromError = !emailFrom
            ? requiredFieldMessage
            : validateEmail(emailFrom)
              ? ""
              : emailFieldMessage;
        setEmailFromError(localEmailFromError);
        return !!(localEmailToError || localEmailFromError);
    };

    const resetFields = () => {
        setEmailTo("");
        setEmailToError("");
        setEmailFrom("");
        setEmailFromError("");
        setSubject(`${translate(`${entityName}`)} #${entityId}`);
        setMessage("");
        // XNG-Changes
        setShowSuccessMsg(false);
        setErrorMsg("");
    };

    const emailButtonClickHandler = () => {
        resetFields();
        setModalIsOpen(true);
    };

    const emailToChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailTo(event.target.value);
        setEmailToError(!event.target.value ? requiredFieldMessage : "");
    };

    const emailFromChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEmailFrom(event.target.value);
        setEmailFromError(!event.target.value ? requiredFieldMessage : "");
    };

    const subjectChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSubject(event.target.value);
    };

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const sendButtonClickHandler = () => {
        // Xng-Changes
        const errors = xValidateOnSubmit(formId);

        if (checkForErrors() || errors.length > 0) {
            return;
        }
        // XNG-Changes
        setShowSuccessMsg(false);
        setErrorMsg("");
        setIsSending(true);
        shareEntity({
            shareEntityModel: {
                entityId,
                entityName,
                emailTo,
                emailFrom,
                subject,
                message,
                ...extraProperties,
            } as ShareEntityModel,
            url,
            onSuccess: () => {
                // XNG-Changes:from classic
                // setModalIsOpen(false);
                setIsSending(false);
                // toasterContext.addToast({ body: siteMessage("Entity_Share_Success"), messageType: "success" });
                setShowSuccessMsg(true);
            },
            // XNG-Changes
            onError: (errorMessage: string) => {
                setIsSending(false);
                setShowSuccessMsg(false);
                setErrorMsg(errorMessage || siteMessage("Entity_Share_Error"));
            },
            generateAttachmentFromWebpage,
        } as ShareEntityParameter & HasOnError<string>);
    };

    // XNG-Changes add loading spinner
    useEffect(() => {
        if (isSending) {
            xLoadingOverlay({ key: "add", id: "share_list_modal" });
        } else {
            xLoadingOverlay({ key: "clear", id: "share_list_modal" });
        }
    }, [isSending]);

    // XNG-Changes
    const onClickPrint = () => {
        setModalIsOpen(false);
        if (isQuote) {
            window.open(`${QuotePdfAttachmentUrl}?quotenumber=${encodeURIComponent(entityId)}`, "_blank");
        } else if (isInvoice) {
            window.open(`${OrderPdfAttachmentUrl}?invoicenumber=${encodeURIComponent(entityId)}`, "_blank");
        } else {
            setTimeout(() => {
                openPrintDialog();
            }, 500);
        }
    };

    return (
        <>
            {variant === "clickable" && (
                <Clickable
                    {...styles.clickable}
                    onClick={emailButtonClickHandler}
                    data-test-selector="shareEntity_email"
                    aria-label="Email Clickable"
                >
                    {translate("Email")}
                </Clickable>
            )}
            {variant === "button" && (
                <Button
                    {...styles.button}
                    onClick={emailButtonClickHandler}
                    data-test-selector="shareEntity_email"
                    aria-label="Email Button"
                >
                    {translate("Email")}
                </Button>
            )}
            <Modal
                {...styles.modal}
                headline={`${translate(`Email ${entityName}`)} #${entityId}`}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <GridContainer {...styles.container} data-test-selector="shareEntity_modal">
                    <GridItem {...styles.printButtonGridItem}>
                        <Button onClick={onClickPrint}>{printBtnLabel}</Button>
                    </GridItem>
                    <GridItem {...styles.emailToGridItem}>
                        <TextField
                            label={`${translate("Email To")}`}
                            required
                            value={emailTo}
                            error={emailToError}
                            onChange={emailToChangeHandler}
                            data-test-selector="shareEntity_emailTo"
                            type="email"
                            {...styles.emailToTextField}
                            // XNG-Changes
                            placeholder={translate("eg. example@gmail.com")}
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.emailFromGridItem}>
                        <TextField
                            label={`${translate("Email From")}`}
                            required
                            value={emailFrom}
                            error={emailFromError}
                            onChange={emailFromChangeHandler}
                            data-test-selector="shareEntity_emailFrom"
                            type="email"
                            {...styles.emailFromTextField}
                            // XNG-Changes
                            placeholder={translate("eg. example@gmail.com")}
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.subjectGridItem}>
                        <TextField
                            label={translate("Subject")}
                            value={subject}
                            onChange={subjectChangeHandler}
                            data-test-selector="shareEntity_subject"
                            {...styles.subjectTextField}
                            // XNG-Changes
                            placeholder={translate("eg. Order ######")}
                            {...{ formId }}
                        />
                    </GridItem>
                    <GridItem {...styles.messageGridItem}>
                        <TextArea
                            label={translate("Message")}
                            value={message}
                            onChange={messageChangeHandler}
                            data-test-selector="shareEntity_message"
                            {...styles.messageTextArea}
                            // XNG-Changes
                            placeholder={translate("Message")}
                            {...{ formId }}
                        />
                    </GridItem>
                    {errorMsg && (
                        <GridItem {...styles.messageGridItem}>
                            <Notify type="danger" {...styles.notifyMsg}>
                                {errorMsg}
                            </Notify>
                        </GridItem>
                    )}
                    {showSuccessMsg && (
                        <GridItem {...styles.messageGridItem}>
                            <Notify type="success" {...styles.notifyMsg}>
                                {siteMessage("Entity_Share_Success")}
                            </Notify>
                        </GridItem>
                    )}
                </GridContainer>
                <StyledWrapper {...styles.buttonsWrapper}>
                    <Button
                        {...styles.cancelButton}
                        onClick={modalCloseHandler}
                        data-test-selector="shareEntity_cancel"
                        aria-label="Cancel"
                    >
                        {translate("Cancel")}
                    </Button>
                    <Button
                        {...styles.sendButton}
                        disabled={isSending}
                        onClick={sendButtonClickHandler}
                        data-test-selector="shareEntity_submit"
                        aria-label="Send"
                    >
                        {translate("Send")}
                    </Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default connect(null, mapDispatchToProps)(ShareEntityButton);
